import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexAnnotations, ApexOptions } from "apexcharts";
import {
  Gender,
  normalizeGender,
  getWhoChartSeries,
  WhoIndicator,
  WhoChartPoint
} from "../../constants/whoData";

export type ChartTitle = "Height (cm)" | "Weight (kg)" | "BMI";

export interface StudentDataPoint {
  age: number;
  value: number;
  date?: string;
  name?: string;
  gender?: string;
}

interface Props {
  title: ChartTitle;
  unit: string;
  gender?: Gender | string | null;
  studentData: StudentDataPoint[];
  studentName?: string;
  whoData?: any[]; // Kept for backward compatibility
}

const WhoLineChart: React.FC<Props> = ({
  title,
  unit,
  gender,
  studentData,
  studentName
}) => {
  const normalizedGender: Gender = normalizeGender(gender) || "Male";
  const indicator: WhoIndicator =
    title === "Height (cm)" ? "height" :
    title === "Weight (kg)" ? "weight" : "bmi";

  // 1. Get official sex-specific reference series (5 to 19y for Height, BMI, and CDC Weight)
  const refPoints: WhoChartPoint[] = useMemo(() => {
    return getWhoChartSeries(
      indicator,
      normalizedGender,
      5,
      19,
      0.5
    );
  }, [indicator, normalizedGender]);

  // 2. Filter valid student measurements
  const validStudentData = useMemo(() => {
    return (studentData || []).filter(
      d => typeof d.value === "number" && !isNaN(d.value) && d.value > 0
    );
  }, [studentData]);

  // 3. Dynamic Axis Ranges
  const defaultYConfig: Record<ChartTitle, { min: number; max: number }> = {
    "Height (cm)": { min: 90, max: 200 },
    "Weight (kg)": { min: 10, max: 110 },
    "BMI": { min: 10, max: 32 }
  };

  const defaultAxis = defaultYConfig[title] || { min: 0, max: 100 };

  const studentValues = validStudentData.map(d => d.value);
  const refP97Values = refPoints.map(p => p.p97);
  const refP3Values = refPoints.map(p => p.p3);

  const allMinY = [...studentValues, ...refP3Values, defaultAxis.min];
  const allMaxY = [...studentValues, ...refP97Values, defaultAxis.max];

  const yMin = Math.max(0, Math.floor(Math.min(...allMinY) * 0.95));
  const yMax = Math.ceil(Math.max(...allMaxY) * 1.05);

  const studentAges = validStudentData
    .map(d => d.age)
    .filter(a => typeof a === "number" && !isNaN(a) && a > 0);
  const maxStudentAge = studentAges.length > 0 ? Math.max(...studentAges) : 19;
  const minStudentAge = studentAges.length > 0 ? Math.min(...studentAges) : 5;

  const xMin = Math.min(5, Math.floor(minStudentAge));
  const xMax = Math.max(19, Math.ceil(maxStudentAge));

  // 4. Build the 6 Series: P3, P15, P50, P85, P97 + Student (Actual)
  const series = useMemo(() => {
    return [
      {
        name: "3rd percentile",
        data: refPoints.map(p => ({ x: p.age, y: +p.p3.toFixed(2) })),
        color: "#64748B" // Slate gray
      },
      {
        name: "15th percentile",
        data: refPoints.map(p => ({ x: p.age, y: +p.p15.toFixed(2) })),
        color: "#EF4444" // Coral crimson
      },
      {
        name: "50th percentile",
        data: refPoints.map(p => ({ x: p.age, y: +p.p50.toFixed(2) })),
        color: "#10B981" // Emerald teal median
      },
      {
        name: "85th percentile",
        data: refPoints.map(p => ({ x: p.age, y: +p.p85.toFixed(2) })),
        color: "#F59E0B" // Amber gold
      },
      {
        name: "97th percentile",
        data: refPoints.map(p => ({ x: p.age, y: +p.p97.toFixed(2) })),
        color: "#8B5CF6" // Royal violet
      },
      {
        name: studentName ? `${studentName} (Actual)` : "Student (Actual)",
        data: validStudentData.map(d => ({
          x: typeof d.age === "number" && !isNaN(d.age) && d.age > 0 ? +d.age.toFixed(2) : 5,
          y: +d.value.toFixed(2)
        })),
        color: "#0284C7" // Vibrant electric ocean blue
      }
    ];
  }, [refPoints, validStudentData, studentName]);

  // 5. Minor Grid Lines
  const minorXGrid = [];
  for (let age = xMin; age <= xMax; age += 1) {
    minorXGrid.push({
      x: age,
      borderColor: "#F1F5F9",
      strokeDashArray: 2
    });
  }

  // 6. WHO Normal Range (-2 SD to +1 SD) for BMI
  const isBMI = indicator === "bmi";
  const yAxisAnnotations: ApexAnnotations["yaxis"] = [];

  if (isBMI && refPoints.length > 0) {
    const minSd2Neg = Math.min(...refPoints.map(p => p.sd2neg || p.p3));
    const maxSd1 = Math.max(...refPoints.map(p => p.sd1 || p.p85));

    yAxisAnnotations.push({
      y: minSd2Neg,
      y2: maxSd1,
      fillColor: "#10B981",
      opacity: 0.12,
      borderColor: "#10B981",
      strokeDashArray: 4,
      label: {
        text: "WHO Normal Range (-2 SD to +1 SD)",
        style: {
          fontSize: "11px",
          fontWeight: 600,
          color: "#065F46",
          background: "#D1FAE5"
        },
        position: "left"
      }
    });
  }

  // 7. ApexCharts Options
  const options: ApexOptions = {
    chart: {
      type: "line",
      fontFamily: "Inter, Roboto, 'Segoe UI', sans-serif",
      animations: {
        enabled: false
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: { enabled: true }
    },

    stroke: {
      curve: "smooth",
      width: [
        1.8, // 3rd percentile
        1.8, // 15th percentile
        2.8, // 50th percentile (prominent median)
        1.8, // 85th percentile
        1.8, // 97th percentile
        validStudentData.length === 1 ? 0 : 3.5 // Student (Actual)
      ],
      dashArray: [4, 2, 0, 2, 4, 0]
    },

    markers: {
      size: [0, 0, 0, 0, 0, validStudentData.length === 1 ? 9 : 7],
      colors: ["transparent", "transparent", "transparent", "transparent", "transparent", "#0284C7"],
      strokeColors: "#FFFFFF",
      strokeWidth: 3,
      hover: {
        size: 11,
        sizeOffset: 3
      }
    },

    xaxis: {
      type: "numeric",
      min: xMin,
      max: xMax,
      tickAmount: Math.min(20, Math.ceil(xMax - xMin)),
      title: {
        text: "Age (Years)",
        style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
      },
      axisBorder: { show: true, color: "#E2E8F0" },
      axisTicks: { show: true, color: "#E2E8F0" },
      labels: {
        style: { colors: "#64748B", fontSize: "11px", fontWeight: 500 },
        formatter: val => {
          const num = Number(val);
          return num % 1 === 0 ? num.toString() : num.toFixed(1);
        }
      }
    },

    yaxis: {
      min: yMin,
      max: yMax,
      title: {
        text: `${title} (${normalizedGender})`,
        style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
      },
      axisBorder: { show: true, color: "#E2E8F0" },
      axisTicks: { show: true, color: "#E2E8F0" },
      labels: {
        style: { colors: "#64748B", fontSize: "11px", fontWeight: 500 },
        formatter: val => Number(val).toFixed(0)
      }
    },

    grid: {
      show: true,
      borderColor: "#F1F5F9",
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } }
    },

    annotations: {
      xaxis: minorXGrid,
      yaxis: yAxisAnnotations
    },

    tooltip: {
      shared: false,
      intersect: false,
      custom: ({ series: sList, seriesIndex, dataPointIndex, w }) => {
        const isStudentSeries = seriesIndex === 5;
        if (isStudentSeries) {
          const pt = validStudentData[dataPointIndex];
          if (!pt) return "";
          const dateStr = pt.date && !isNaN(new Date(pt.date).getTime())
            ? new Date(pt.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : "-";
          const sName = pt.name || studentName || "Student";
          const sGender = pt.gender || normalizedGender;
          const sAge = typeof pt.age === "number" ? pt.age.toFixed(2) : pt.age;
          return `
            <div style="padding: 12px 16px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.6; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #0284C7; border-radius: 10px; box-shadow: 0 10px 25px -5px rgba(2, 132, 199, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.08);">
              <div style="font-weight: 700; color: #0F172A; margin-bottom: 6px; border-bottom: 1px solid #F1F5F9; padding-bottom: 4px; display: flex; align-items: center; justify-content: space-between; gap: 8px;">
                <span>${sName}</span>
                <span style="font-size: 10px; font-weight: 600; padding: 2px 6px; background: #E0F2FE; color: #0284C7; border-radius: 4px;">Measured</span>
              </div>
              <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Date:</span> <b style="color: #1E293B;">${dateStr}</b></div>
              <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Age:</span> <b style="color: #1E293B;">${sAge} yrs</b></div>
              <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Gender:</span> <b style="color: #1E293B;">${sGender}</b></div>
              <div style="margin-top: 6px; padding-top: 4px; border-top: 1px dashed #E2E8F0; display: flex; justify-content: space-between; align-items: center; gap: 12px;">
                <span style="color: #0284C7; font-weight: 600;">${title}:</span>
                <b style="font-size: 14px; color: #0284C7;">${pt.value} ${unit}</b>
              </div>
            </div>
          `;
        }

        const seriesName = w.globals.seriesNames[seriesIndex];
        const ageVal = w.globals.seriesX[seriesIndex]?.[dataPointIndex];
        const val = sList[seriesIndex]?.[dataPointIndex];
        const sourceLabel = indicator === "weight" ? "CDC Reference" : "WHO Reference";
        const color = w.globals.colors[seriesIndex] || "#64748B";
        return `
          <div style="padding: 10px 14px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.5; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #E2E8F0; border-left: 3px solid ${color}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);">
            <div style="font-weight: 600; color: #1E293B; margin-bottom: 2px;">${seriesName} (${normalizedGender})</div>
            <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Age:</span> <b style="color: #1E293B;">${typeof ageVal === "number" ? ageVal.toFixed(1) : ageVal} yrs</b></div>
            <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>${sourceLabel}:</span> <b style="color: #1E293B;">${val} ${unit}</b></div>
          </div>
        `;
      }
    },

    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, Roboto, sans-serif",
      fontWeight: 500,
      itemMargin: { horizontal: 8, vertical: 4 }
    }
  };

  return (
    <div>
      <ReactApexChart
        options={options}
        series={series}
        type="line"
        height={430}
      />
      <div style={{
        textAlign: "center",
        fontSize: "11px",
        color: "#64748B",
        marginTop: "6px",
        padding: "4px 12px",
        background: "#F8FAFC",
        borderRadius: "6px",
        border: "1px solid #F1F5F9",
        display: "inline-block",
        width: "100%",
        boxSizing: "border-box"
      }}>
        {indicator === "weight"
          ? "Reference: CDC Weight-for-Age Reference (2–20 years) • P3, P15, P50 (Median), P85, P97"
          : "Reference: WHO 2007 Child Growth Reference (5–19 years) • P3, P15, P50 (Median), P85, P97"}
      </div>
    </div>
  );
};

export default WhoLineChart;
